import { useState } from 'react';
import DatePicker from '../common/DatePicker';
import { Calendar } from 'lucide-react';

export type DatePeriod = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'custom';

interface ServiceDateFilterProps {
  value: DatePeriod;
  customRange?: { startDate: string; endDate: string };
  onChange: (period: DatePeriod, customRange?: { startDate: string; endDate: string }) => void;
}

const QUICK_FILTERS: { value: DatePeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
];

export default function ServiceDateFilter({ value, customRange, onChange }: ServiceDateFilterProps) {
  const [showCustom, setShowCustom] = useState(value === 'custom');
  const [localStart, setLocalStart] = useState(customRange?.startDate || '');
  const [localEnd, setLocalEnd] = useState(customRange?.endDate || '');

  const handlePeriodClick = (period: DatePeriod) => {
    setShowCustom(false);
    onChange(period);
  };

  const handleCustomApply = () => {
    if (localStart && localEnd) {
      onChange('custom', { startDate: localStart, endDate: localEnd });
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Quick filter pills */}
      {QUICK_FILTERS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => handlePeriodClick(opt.value)}
          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
            value === opt.value && !showCustom
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {opt.label}
        </button>
      ))}

      {/* Custom toggle */}
      <button
        onClick={() => setShowCustom(!showCustom)}
        className={`p-1.5 rounded-full transition-colors ${
          showCustom ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}
        title="Custom date range"
      >
        <Calendar className="w-3.5 h-3.5" />
      </button>

      {/* Custom date inputs */}
      {showCustom && (
        <div className="flex items-center gap-1.5">
          <DatePicker
            value={localStart}
            onChange={setLocalStart}
            wrapperClassName="w-32"
            className="!px-2 !py-1 !text-xs !border-gray-200"
          />
          <span className="text-gray-400 text-xs">-</span>
          <DatePicker
            value={localEnd}
            onChange={setLocalEnd}
            wrapperClassName="w-32"
            className="!px-2 !py-1 !text-xs !border-gray-200"
          />
          <button
            onClick={handleCustomApply}
            disabled={!localStart || !localEnd}
            className="text-xs bg-purple-600 text-white px-2.5 py-1 rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            Go
          </button>
        </div>
      )}
    </div>
  );
}
